import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api, formatCurrency, formatDateBR, formatCPF } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Printer, Loader2 } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

const LOGO_URL =
    "https://customer-assets-eiarnc6j.emergentagent.net/job_carpool-manager-6/artifacts/qyxqg7dd_logo%20Padr%C3%A3o.png";

function numberToWordsBR(value) {
    if (value == null) return "";
    const num = Number(value);
    if (isNaN(num)) return "";
    const reais = Math.floor(num);
    const cents = Math.round((num - reais) * 100);
    const unidades = ["", "um", "dois", "três", "quatro", "cinco", "seis", "sete", "oito", "nove",
        "dez", "onze", "doze", "treze", "quatorze", "quinze", "dezesseis", "dezessete", "dezoito", "dezenove"];
    const dezenas = ["", "", "vinte", "trinta", "quarenta", "cinquenta", "sessenta", "setenta", "oitenta", "noventa"];
    const centenas = ["", "cento", "duzentos", "trezentos", "quatrocentos", "quinhentos", "seiscentos", "setecentos", "oitocentos", "novecentos"];

    const toWords = (n) => {
        if (n === 0) return "zero";
        if (n === 100) return "cem";
        let parts = [];
        const c = Math.floor(n / 100);
        const d = Math.floor((n % 100) / 10);
        const u = n % 10;
        const dUnit = n % 100;
        if (c > 0) parts.push(centenas[c]);
        if (dUnit < 20 && dUnit > 0) {
            parts.push(unidades[dUnit]);
        } else {
            if (d > 0) parts.push(dezenas[d]);
            if (u > 0) parts.push(unidades[u]);
        }
        return parts.join(" e ");
    };

    let result = "";
    if (reais > 0) {
        const thousands = Math.floor(reais / 1000);
        const rest = reais % 1000;
        if (thousands > 0) {
            result += (thousands === 1 ? "mil" : `${toWords(thousands)} mil`);
            if (rest > 0) result += rest < 100 ? " e " : " ";
        }
        if (rest > 0 || thousands === 0) result += toWords(rest);
        result += reais === 1 ? " real" : " reais";
    }
    if (cents > 0) {
        result += reais > 0 ? " e " : "";
        result += `${toWords(cents)} ${cents === 1 ? "centavo" : "centavos"}`;
    }
    if (!result) result = "zero reais";
    return result.charAt(0).toUpperCase() + result.slice(1);
}

export default function FreightPrint() {
    const { id } = useParams();
    const { user } = useAuth();
    const [frete, setFrete] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);

    useEffect(() => {
        api.get(`/freights/${id}`).then((res) => {
            setFrete(res.data);
            setLoading(false);
        }).catch(() => { setError(true); setLoading(false); });
    }, [id]);

    const doPrint = () => window.print();

    if (loading) {
        return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>;
    }
    if (error || !frete) {
        return (
            <div className="min-h-screen flex items-center justify-center flex-col gap-4">
                <div className="text-muted-foreground">Frete não encontrado</div>
                <Button onClick={() => window.close()}>Fechar</Button>
            </div>
        );
    }

    const today = new Date().toLocaleDateString("pt-BR");

    return (
        <div className="print-root min-h-screen bg-gray-100 p-8" data-testid="freight-print-page">
            <div className="no-print max-w-3xl mx-auto mb-4 flex items-center justify-between">
                <Button variant="outline" onClick={() => window.close()}>Fechar</Button>
                <Button onClick={doPrint} className="gap-2" data-testid="btn-print-freight-now">
                    <Printer className="w-4 h-4" /> Imprimir
                </Button>
            </div>

            <div className="ficha max-w-3xl mx-auto bg-white shadow-md">
                <div className="flex items-center gap-4 p-6 border-b-2 border-black">
                    <div className="w-20 h-20 bg-emerald-900 rounded-lg flex items-center justify-center p-1 shrink-0 border-2 border-emerald-800 shadow-md">
                        <img src={LOGO_URL} alt="COOMFLONA" className="w-full h-full object-contain" />
                    </div>
                    <div className="flex-1 text-center">
                        <div className="font-extrabold text-lg leading-tight">COOPERATIVA MISTA DA FLONA DO TAPAJÓS – COOMFLONA</div>
                        <div className="text-sm mt-1">CNPJ: 07.315.165/0001-00 – Insc. Est: 15-249-128-7</div>
                        <div className="text-sm">coomflona@hotmail.com</div>
                    </div>
                </div>

                <div className="text-center py-4 border-b border-black">
                    <div className="font-extrabold text-lg">CONTROLE DE FRETE</div>
                    <div className="text-sm mt-1">Setor de Transporte</div>
                </div>

                <div className="p-8 space-y-5 text-base">
                    <div className="text-xs font-semibold uppercase tracking-wider text-gray-700 border-b border-gray-400 pb-1">Cliente</div>
                    <Field label="Nome" value={frete.cliente_nome} />
                    <div className="grid grid-cols-2 gap-6">
                        <Field label="CPF / CNPJ" value={frete.cliente_documento || "—"} />
                        <Field label="Telefone" value={frete.telefone || "—"} />
                    </div>

                    <div className="text-xs font-semibold uppercase tracking-wider text-gray-700 border-b border-gray-400 pb-1 pt-3">Rota e carga</div>
                    <div className="grid grid-cols-2 gap-6">
                        <Field label="Origem" value={frete.origem} />
                        <Field label="Destino" value={frete.destino} />
                    </div>
                    <div className="grid grid-cols-2 gap-6">
                        <Field label="Tipo de carga" value={frete.tipo_carga} />
                        <Field label="Peso / volume" value={frete.peso_volume || "—"} />
                    </div>

                    <div className="text-xs font-semibold uppercase tracking-wider text-gray-700 border-b border-gray-400 pb-1 pt-3">Motorista e veículo</div>
                    <Field label="Motorista" value={frete.responsavel_nome} />
                    <div className="grid grid-cols-3 gap-6">
                        <Field label="CPF" value={formatCPF(frete.cpf) || "—"} />
                        <Field label="Veículo" value={frete.veiculo_modelo || "—"} />
                        <Field label="Placa" value={frete.veiculo_placa} />
                    </div>

                    <div className="text-xs font-semibold uppercase tracking-wider text-gray-700 border-b border-gray-400 pb-1 pt-3">Data e valor</div>
                    <Field label="Data do frete" value={formatDateBR(frete.data)} />
                    <div className="mt-4 pt-3 border-t border-black/40">
                        <div className="text-xs uppercase tracking-wider text-gray-600 font-semibold">Valor do frete</div>
                        <div className="text-3xl font-extrabold mt-1">{formatCurrency(frete.valor)}</div>
                        <div className="text-sm italic mt-1 text-gray-700">({numberToWordsBR(frete.valor)})</div>
                    </div>

                    {frete.observacoes && (
                        <div className="mt-4 pt-3 border-t border-black/20">
                            <div className="text-xs uppercase tracking-wider text-gray-600 font-semibold">Observações</div>
                            <div className="mt-1 whitespace-pre-wrap">{frete.observacoes}</div>
                        </div>
                    )}
                </div>

                <div className="px-8 pb-8 pt-12 grid grid-cols-3 gap-6">
                    <div className="text-center">
                        <div className="border-t border-black pt-2 text-sm font-medium">Cliente / Contratante</div>
                        <div className="text-xs text-gray-600 mt-1">{frete.cliente_nome}</div>
                    </div>
                    <div className="text-center">
                        <div className="border-t border-black pt-2 text-sm font-medium">Motorista</div>
                        <div className="text-xs text-gray-600 mt-1">{frete.responsavel_nome}</div>
                    </div>
                    <div className="text-center">
                        <div className="border-t border-black pt-2 text-sm font-medium">Setor de Transporte</div>
                        <div className="text-xs text-gray-600 mt-1">{user?.name || "—"}</div>
                    </div>
                </div>

                <div className="px-8 pb-4 flex justify-between text-[10px] text-gray-500 border-t border-gray-300 pt-3">
                    <div>Emitido em: {today} {user?.name ? `— por ${user.name}` : ""}</div>
                    <div>Frete nº: {frete.id.slice(0, 8).toUpperCase()}</div>
                </div>
            </div>

            <style>{`
                @media print {
                    body { background: white !important; }
                    .no-print { display: none !important; }
                    .print-root { padding: 0 !important; background: white !important; }
                    .ficha { box-shadow: none !important; max-width: 100% !important; }
                    @page { size: A4; margin: 15mm; }
                }
            `}</style>
        </div>
    );
}

function Field({ label, value }) {
    return (
        <div>
            <div className="text-xs uppercase tracking-wider text-gray-600 font-semibold">{label}</div>
            <div className="mt-1 font-medium border-b border-dashed border-gray-400 pb-1 min-h-[24px]">{value || "—"}</div>
        </div>
    );
}
